import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// 🔧 match backend constant
const ANNUAL_DRIVER_CERT_TASK_ID = 57;

const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "application/vnd.ms-outlook", "application/x-msg", "application/CDFV2", "application/x-ole-storage", "application/octet-stream", ""];

const normalizeDateInput = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

export default function AnnualDriverCertificationForm() {
  const navigate = useNavigate();
  const { id: paramId, adcId } = useParams();
  const isNew = !adcId || adcId === "new";

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [profile, setProfile] = useState({
    driver_name: "",
    ssn: "",
    date_of_employment: "",
    home_terminal: "",
    dl_license_no: "",
    dl_province: "",
    dl_expdate: "",
  });

  const [form, setForm] = useState({
    adc_userid: userId,
    adc_no_violations: false,
    adc_violations: [{ date: "", offense: "", location: "", vehicle: "" }],
  });

  const [isSigned, setIsSigned] = useState(false);

  // attachments
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);

  // ============================
  // Load profile (autofill)
  // ============================
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/annual-driver-certifications/profile/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const p = res.data || {};
        setProfile({
          driver_name: p.driver_name || "",
          ssn: p.ssn || "",
          date_of_employment: normalizeDateInput(p.date_of_employment),
          home_terminal: p.home_terminal || "",
          dl_license_no: p.dl_license_no || "",
          dl_province: p.dl_province || "",
          dl_expdate: normalizeDateInput(p.dl_expdate),
        });
      } catch (err) {
        console.error("❌ ADC profile fetch error", err);
      }
    };
    fetchProfile();
  }, [userId]);

  // ============================
  // Load record (edit)
  // ============================
  useEffect(() => {
    const load = async () => {
      if (isNew) return;
      try {
        const res = await axios.get(
          `${API_BASE}/api/annual-driver-certifications/detail/${adcId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const d = res.data || {};
        setForm({
          adc_userid: d.adc_userid || userId,
          adc_no_violations: !!d.adc_no_violations,
          adc_violations:
            Array.isArray(d.adc_violations) && d.adc_violations.length
              ? d.adc_violations.map((v) => ({
                  date: normalizeDateInput(v.date),
                  offense: v.offense || "",
                  location: v.location || "",
                  vehicle: v.vehicle || "",
                }))
              : [{ date: "", offense: "", location: "", vehicle: "" }],
        });
      } catch (err) {
        console.error("❌ ADC detail fetch error", err);
      }
    };
    load();
  }, [isNew, adcId, userId]);

  // ============================
  // Load attachments (edit)
  // ============================
  useEffect(() => {
    const loadAttachments = async () => {
      if (isNew) return;
      try {
        const res = await axios.get(
          `${API_BASE}/api/annual-driver-certifications/attachments/${adcId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setExistingAttachments(res.data || []);
      } catch (err) {
        console.error("❌ ADC attachments fetch error", err);
      }
    };
    loadAttachments();
  }, [isNew, adcId]);

  // ============================
  // Lock if signed, consistent with other signed task forms.
  // ============================
  useEffect(() => {
    const fetchSignStatus = async () => {
      if (!adcId || adcId === "new") return;
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const refKey = `${ANNUAL_DRIVER_CERT_TASK_ID}_${adcId}`;
        const taskKey = "annual_driver_certification";
        const status =
          res.data.status?.[refKey] ??
          res.data.status?.[taskKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("❌ ADC sign status fetch error", err);
      }
    };
    fetchSignStatus();
  }, [userId, adcId]);

  const violations = useMemo(
    () => form.adc_violations || [],
    [form.adc_violations]
  );

  const setViolation = (idx, key, value) => {
    const next = violations.map((v, i) =>
      i === idx ? { ...v, [key]: value } : v
    );
    setForm((p) => ({ ...p, adc_violations: next }));
  };

  const addRow = () => {
    setForm((p) => ({
      ...p,
      adc_violations: [
        ...(p.adc_violations || []),
        { date: "", offense: "", location: "", vehicle: "" },
      ],
    }));
  };

  const removeRow = (idx) => {
    const next = violations.filter((_, i) => i !== idx);
    setForm((p) => ({
      ...p,
      adc_violations: next.length
        ? next
        : [{ date: "", offense: "", location: "", vehicle: "" }],
    }));
  };

  const validateFiles = (files) => {
    const list = Array.from(files || []);
    const bad = list.find((f) => !allowedTypes.includes(f.type));
    if (bad) {
      alert("Incorrect file format.");
      return [];
    }
    return list;
  };

  // ============================
  // Save (create/update)
  // ============================
  const handleSave = async (e) => {
    e.preventDefault();

    const payload = {
      adc_userid: userId,
      adc_no_violations: !!form.adc_no_violations,
      adc_violations: (form.adc_violations || []).filter((r) => {
        // keep rows that have at least one field
        return (
          (r.date || r.offense || r.location || r.vehicle).toString().trim() !==
          ""
        );
      }),
    };

    try {
      if (isNew) {
        const res = await axios.post(
          `${API_BASE}/api/annual-driver-certifications`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        alert("✔️ Created successfully!");
        navigate(
          `/annual-driver-certifications/${userId}/detail/${res.data.id}`
        );
      } else {
        await axios.put(
          `${API_BASE}/api/annual-driver-certifications/${adcId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        alert("✔️ Updated successfully!");
      }
    } catch (err) {
      console.error("❌ ADC save error", err);
      alert("Save failed. Please try again.");
    }
  };

  // ============================
  // Upload attachments (multi)
  // ============================
  const uploadAttachments = async () => {
    if (isNew) return alert("Please save first.");
    if (!pendingFiles.length) return;

    const fd = new FormData();
    fd.append("adc_id", adcId);
    fd.append("adc_userid", userId);
    pendingFiles.forEach((f) => fd.append("files", f));

    try {
      await axios.post(
        `${API_BASE}/api/annual-driver-certifications/attachments/upload`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      alert("✔️ Saved successfully!");
      setPendingFiles([]);

      const res = await axios.get(
        `${API_BASE}/api/annual-driver-certifications/attachments/${adcId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setExistingAttachments(res.data || []);
    } catch (err) {
      console.error("❌ ADC attachment upload error", err);
      alert("Upload failed.");
    }
  };

  const deleteAttachment = async (adca_id) => {
    const attachment = existingAttachments.find((item) => item.adca_id === adca_id);
    if (
      !window.confirm(
        `Delete uploaded file "${attachment?.adca_original || attachment?.adca_file || "attachment"}"?`,
      )
    ) {
      return;
    }
    try {
      await axios.delete(
        `${API_BASE}/api/annual-driver-certifications/attachments/${adca_id}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setExistingAttachments((p) => p.filter((x) => x.adca_id !== adca_id));
    } catch (err) {
      console.error("❌ ADC attachment delete error", err);
      alert("Delete failed.");
    }
  };

  // After signing, move the task into review status.
  const handleSignComplete = async (completed) => {
    if (!completed) return;
    try {
      await axios.put(
        `${API_BASE}/api/annual-driver-certifications/complete/${adcId}`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setIsSigned(true);
    } catch (err) {
      console.error("❌ ADC complete error", err);
    }
  };

  // const goBack = () => navigate(-1);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: 18, fontWeight: 500 }}>
          Annual Driver Certification of Violations
        </span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => navigate(`/annual-driver-certifications/${userId}`)}
          >
            View List
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4">
        <div className="text-center mb-3">
          <h3 className="mb-0">
            COMPLETED BY DRIVER - CERTIFICATION OF VIOLATIONS
          </h3>
        </div>

        {/* Header fields (read-only) */}
        <div className="card p-3 mb-3">
          <div className="row g-3">
            <div className="col-md-6">
              <label>Driver Name</label>
              <input
                className="form-control"
                value={profile.driver_name}
                disabled
              />
            </div>
            <div className="col-md-3">
              <label>Social Security Number</label>
              <input className="form-control" value={profile.ssn} disabled />
            </div>
            <div className="col-md-3">
              <label>Date of Employment</label>
              <input
                className="form-control"
                type="date"
                value={profile.date_of_employment}
                disabled
              />
            </div>

            <div className="col-md-6">
              <label>Home Terminal (City and State)</label>
              <input
                className="form-control"
                value={profile.home_terminal}
                disabled
              />
            </div>
            <div className="col-md-3">
              <label>Driver’s License Number</label>
              <input
                className="form-control"
                value={profile.dl_license_no}
                disabled
              />
            </div>
            <div className="col-md-1">
              <label>State</label>
              <input
                className="form-control"
                value={profile.dl_province}
                disabled
              />
            </div>
            <div className="col-md-2">
              <label>Expiration Date</label>
              <input
                className="form-control"
                type="date"
                value={profile.dl_expdate}
                disabled
              />
            </div>
          </div>
        </div>

        <form onSubmit={handleSave}>
          {/* No violations checkbox */}
          <div className="card p-3 mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="noViolations"
                checked={!!form.adc_no_violations}
                disabled={!isAdmin && isSigned}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    adc_no_violations: e.target.checked,
                    adc_violations: e.target.checked
                      ? [{ date: "", offense: "", location: "", vehicle: "" }]
                      : p.adc_violations,
                  }))
                }
              />
              <label className="form-check-label" htmlFor="noViolations">
                Check this box if you have had no violations in the past 12
                months.
              </label>
            </div>
          </div>

          {/* Violations table */}
          <div className="card p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Violations</h5>
              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                onClick={addRow}
                disabled={(!isAdmin && isSigned) || form.adc_no_violations}
              >
                ➕ Add Row
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "15%" }}>Date</th>
                    <th>Offense</th>
                    <th style={{ width: "25%" }}>Location</th>
                    <th style={{ width: "20%" }}>Type of Vehicle Operated</th>
                    <th style={{ width: "8%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {violations.map((v, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="date"
                          className="form-control"
                          value={v.date || ""}
                          disabled={
                            (!isAdmin && isSigned) || form.adc_no_violations
                          }
                          onChange={(e) =>
                            setViolation(idx, "date", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={v.offense || ""}
                          disabled={
                            (!isAdmin && isSigned) || form.adc_no_violations
                          }
                          onChange={(e) =>
                            setViolation(idx, "offense", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={v.location || ""}
                          disabled={
                            (!isAdmin && isSigned) || form.adc_no_violations
                          }
                          onChange={(e) =>
                            setViolation(idx, "location", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={v.vehicle || ""}
                          disabled={
                            (!isAdmin && isSigned) || form.adc_no_violations
                          }
                          onChange={(e) =>
                            setViolation(idx, "vehicle", e.target.value)
                          }
                        />
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          disabled={
                            (!isAdmin && isSigned) || form.adc_no_violations
                          }
                          onClick={() => removeRow(idx)}
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!isAdmin && isSigned}
              >
                Save
              </button>
            </div>
          </div>

          {/* Attachments (multi upload, no type) */}
          <div className="card p-3 mb-3">
            <h5>Attachments (multi-file)</h5>

            {isNew ? (
              <div className="alert alert-info mb-0">
                Please save the record before uploading attachments.
              </div>
            ) : (
              <>
                <div className="mb-2">
                  <label>Choose Files (PDF/JPG/PNG)</label>
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    disabled={!isAdmin && isSigned}
                    accept=".pdf,.jpg,.jpeg,.png,.msg"
                    onChange={(e) => {
                      const files = validateFiles(e.target.files);
                      if (files.length)
                        setPendingFiles((p) => [...p, ...files]);
                      e.target.value = "";
                    }}
                  />
                </div>

                {pendingFiles.length > 0 && (
                  <div className="mb-3">
                    <div className="text-muted mb-1">Files Ready To Save</div>
                    <ul className="list-group">
                      {pendingFiles.map((f, idx) => (
                        <li
                          key={`${f.name}-${idx}`}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>{f.name}</span>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                            onClick={() =>
                              setPendingFiles((p) =>
                                p.filter((_, i) => i !== idx)
                              )
                            }
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                        </li>
                      ))}
                    </ul>

                    <div className="d-flex justify-content-end mt-2">
                      <button
                        type="button"
                        className="btn btn-success"
                        title="Save"
                        disabled={
                          !pendingFiles.length || (!isAdmin && isSigned)
                        }
                        onClick={uploadAttachments}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}

                <UploadedAttachmentList
                  title="Uploaded Files"
                  files={existingAttachments}
                  getFileKey={(file) => file.adca_id}
                  getFileName={(file) => file.adca_original || file.adca_file}
                  getFileViewUrl={(file) =>
                    `${API_BASE}/uploads/annual-driver-certifications/${file.adca_file}`
                  }
                  canDelete={Boolean(isAdmin || !isSigned)}
                  onDeleteFile={(file) => deleteAttachment(file.adca_id)}
                />
              </>
            )}
          </div>
        </form>

        {/* Signature & Review (like Work Permit) */}
        <hr className="my-4" />
        {isNew ? (
          <div className="alert alert-info mb-0">
            Save this Annual Driver Certification first, then the signature area
            will be ready to complete.
          </div>
        ) : (
          <>
            <SignatureManager
              taskName="annual_driver_certification"
              taskId={ANNUAL_DRIVER_CERT_TASK_ID}
              referenceId={adcId}
              userId={userId}
              isAdmin={isAdmin}
              onSignComplete={handleSignComplete}
            />
            <ReviewManager
              taskName="annual_driver_certification"
              taskId={ANNUAL_DRIVER_CERT_TASK_ID}
              referenceId={adcId}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
}
